import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Users, 
  Building, 
  Building2,
  BarChart3, 
  CheckCircle, 
  LogOut,
  Globe,
  Settings,
  Download,
  FileText,
  CreditCard,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import SubscriptionManagement from './SubscriptionManagement';
import IdentityProfilesManagement from './IdentityProfilesManagement';
import ConsentManagement from './ConsentManagement';
import logger from '../utils/logger';

const SuperAdminDashboard = () => {
  const { logout } = useAuth();
  const { language, changeLanguage, t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: 'employee',
    empresa_id: '',
    departamento: ''
  });
  const [transferData, setTransferData] = useState({
    user_id: '',
    from_company_id: '',
    to_company_id: ''
  });
  const [transferring, setTransferring] = useState(false);
  const [showTransferConfirm, setShowTransferConfirm] = useState(false);
  const [selectedUserForTransfer, setSelectedUserForTransfer] = useState(null);

  useEffect(() => {
    loadDashboardData();
    loadUsers();
    loadCompanies();
  }, []);

  const loadDashboardData = async () => {
    try {
      const response = await axios.get('/admin/dashboard');
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading dashboard:', error);
      toast.error('Error loading dashboard data');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get('/admin/users');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading users:', error);
      toast.error('Error loading users');
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await axios.get('/admin/companies');
      if (response.data.success) {
        setCompanies(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading companies:', error);
      toast.error('Error loading companies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    
    try {
      // Validate required fields
      if (!newUser.nombre || !newUser.email || !newUser.password || !newUser.empresa_id) {
        toast.error(language === 'es' ? 'Por favor completa todos los campos requeridos' : 'Please complete all required fields');
        setCreatingUser(false);
        return;
      }

      const response = await axios.post('/admin/users/create', {
        nombre: newUser.nombre,
        email: newUser.email,
        password: newUser.password,
        rol: newUser.rol,
        empresa_id: newUser.empresa_id,
        departamento: newUser.departamento || null
      });

      if (response.data.success) {
        toast.success(language === 'es' ? 'Usuario creado exitosamente' : 'User created successfully');
        // Reset form
        setNewUser({
          nombre: '',
          email: '',
          password: '',
          rol: 'employee',
          empresa_id: '',
          departamento: ''
        });
        // Reload users
        loadUsers();
      } else {
        toast.error(response.data.message || (language === 'es' ? 'Error al crear usuario' : 'Error creating user'));
      }
    } catch (error) {
      logger.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al crear usuario' : 'Error creating user'));
    } finally {
      setCreatingUser(false);
    }
  };

  const handleTransferUser = async () => {
    if (!transferData.user_id || !transferData.to_company_id) {
      toast.error(language === 'es' ? 'Por favor selecciona usuario y empresa destino' : 'Please select user and target company');
      return;
    }

    if (transferData.from_company_id === transferData.to_company_id) {
      toast.error(language === 'es' ? 'La empresa origen y destino no pueden ser la misma' : 'Source and target companies cannot be the same');
      return;
    }

    setTransferring(true);
    try {
      const response = await axios.post('/admin/users/transfer', {
        user_id: transferData.user_id,
        from_company_id: transferData.from_company_id,
        to_company_id: transferData.to_company_id
      });

      if (response.data.success) {
        toast.success(language === 'es' 
          ? `Usuario transferido exitosamente. ${response.data.data.assessments_transferred} assessments transferidos.`
          : `User transferred successfully. ${response.data.data.assessments_transferred} assessments transferred.`
        );
        
        // Reset form
        setTransferData({
          user_id: '',
          from_company_id: '',
          to_company_id: ''
        });
        setSelectedUserForTransfer(null);
        setShowTransferConfirm(false);
        
        // Reload data
        loadUsers();
      } else {
        toast.error(response.data.message || (language === 'es' ? 'Error al transferir usuario' : 'Error transferring user'));
      }
    } catch (error) {
      logger.error('Error transferring user:', error);
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al transferir usuario' : 'Error transferring user'));
    } finally {
      setTransferring(false);
    }
  };

  const handleSelectUserForTransfer = (userId) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUserForTransfer(user);
      setTransferData({
        user_id: userId,
        from_company_id: user.empresa_id || '',
        to_company_id: ''
      });
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get('/admin/export-assessments-csv', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `assessments_export_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(language === 'es' ? 'Datos exportados exitosamente' : 'Data exported successfully');
    } catch (error) {
      logger.error('Error exporting CSV:', error);
      toast.error(language === 'es' ? 'Error al exportar datos' : 'Error exporting data');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US');
  };

  const getRoleBadge = (rol) => {
    const roleMap = {
      'individual': { color: 'bg-blue-100 text-blue-800', text: language === 'es' ? 'Individual' : 'Individual' },
      'company_admin': { color: 'bg-purple-100 text-purple-800', text: language === 'es' ? 'Admin Empresa' : 'Company Admin' },
      'employee': { color: 'bg-green-100 text-green-800', text: language === 'es' ? 'Empleado' : 'Employee' },
      'super_admin': { color: 'bg-red-100 text-red-800', text: language === 'es' ? 'Super Admin' : 'Super Admin' }
    };

    const roleInfo = roleMap[rol] || roleMap['individual'];
    return (
      <Badge className={roleInfo.color}>
        {roleInfo.text}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('super_admin_dashboard', 'Panel Super Admin')}
              </h1>
              <p className="text-sm text-gray-600">
                {t('nothr_management_system', 'Sistema de gestión NotHR')}
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Admin Panel Button */}
              <Button
                variant="default"
                size="sm"
                onClick={() => window.location.href = '/admin'}
                className="flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                {t('buttons.admin_panel', 'Panel Admin')}
              </Button>
              
              {/* Language selector */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
              >
                <Globe className="h-4 w-4 mr-2" />
                {language === 'es' ? 'EN' : 'ES'}
              </Button>
              
              {/* Settings */}
              <Button variant="ghost" size="sm">
                <Settings className="h-4 w-4" />
              </Button>
              
              {/* Logout */}
              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Export Button */}
        <div className="mb-6 flex justify-end">
          <Button
            onClick={handleExportCSV}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>{language === 'es' ? 'Exportar Todas las Evaluaciones a CSV' : 'Export All Assessments to CSV'}</span>
          </Button>
        </div>

        {/* Stats Cards */}
        {dashboardData && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {t('total_users', 'Total Usuarios')}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardData.stats.total_users}
                    </p>
                  </div>
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {t('total_companies', 'Total Empresas')}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardData.stats.total_companies}
                    </p>
                  </div>
                  <Building className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {t('total_assessments', 'Total Evaluaciones')}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardData.stats.total_assessments}
                    </p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {t('completed', 'Completadas')}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardData.stats.completed_assessments}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {language === 'es' ? 'Tasa Completado' : 'Completion Rate'}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardData.stats.completion_rate}%
                    </p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-yellow-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Detailed Views */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="grid grid-cols-8 w-full">
            <TabsTrigger value="users">
              {language === 'es' ? 'Usuarios' : 'Users'}
            </TabsTrigger>
            <TabsTrigger value="create-user">
              {language === 'es' ? 'Crear Usuario' : 'Create User'}
            </TabsTrigger>
            <TabsTrigger value="transfer-user">
              {language === 'es' ? 'Transferir Usuario' : 'Transfer User'}
            </TabsTrigger>
            <TabsTrigger value="companies">
              {language === 'es' ? 'Empresas' : 'Companies'}
            </TabsTrigger>
            <TabsTrigger value="subscriptions">
              <CreditCard className="h-4 w-4 mr-1" />
              {language === 'es' ? 'Suscripciones' : 'Subscriptions'}
            </TabsTrigger>
            <TabsTrigger value="identity-profiles">
              <Building2 className="h-4 w-4 mr-1" />
              {language === 'es' ? 'Perfiles Identidad' : 'Identity Profiles'}
            </TabsTrigger>
            <TabsTrigger value="consent" data-testid="consent-tab-trigger">
              <Shield className="h-4 w-4 mr-1" />
              {language === 'es' ? 'Consentimiento' : 'Consent'}
            </TabsTrigger>
            <TabsTrigger value="assessments">
              {language === 'es' ? 'Evaluaciones' : 'Assessments'}
            </TabsTrigger>
          </TabsList>
          
          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'es' ? 'Todos los Usuarios' : 'All Users'}</CardTitle>
                <CardDescription>
                  {language === 'es' ? 'Gestión de usuarios del sistema' : 'System user management'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {users.map((user) => (
                    <div key={user.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-medium text-gray-900">{user.nombre}</h3>
                            {getRoleBadge(user.rol)}
                            <Badge variant={user.activo ? "success" : "destructive"}>
                              {user.activo ? (language === 'es' ? 'Activo' : 'Active') : (language === 'es' ? 'Inactivo' : 'Inactive')}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <span>{user.email}</span>
                            {user.empresa_nombre && (
                              <span>
                                {language === 'es' ? 'Empresa' : 'Company'}: {user.empresa_nombre}
                                {user.empresa_codigo && ` (${user.empresa_codigo})`}
                              </span>
                            )}
                            <span>
                              {language === 'es' ? 'Registrado' : 'Registered'}: {formatDate(user.fecha_registro)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Create User Tab */}
          <TabsContent value="create-user">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'es' ? 'Crear Nuevo Usuario' : 'Create New User'}</CardTitle>
                <CardDescription>
                  {language === 'es' 
                    ? 'Crea un administrador, empleado o candidato para cualquier empresa' 
                    : 'Create an admin, employee or candidate for any company'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateUser} className="space-y-6">
                  {/* Company Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'es' ? 'Empresa' : 'Company'} *
                    </label>
                    <select
                      value={newUser.empresa_id}
                      onChange={(e) => setNewUser({...newUser, empresa_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">
                        {language === 'es' ? 'Seleccionar empresa...' : 'Select company...'}
                      </option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.nombre} ({company.codigo_acceso})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Role Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'es' ? 'Tipo de Usuario' : 'User Type'} *
                    </label>
                    <select
                      value={newUser.rol}
                      onChange={(e) => setNewUser({...newUser, rol: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="company_admin">
                        {language === 'es' ? 'Administrador de Empresa' : 'Company Admin'}
                      </option>
                      <option value="employee">
                        {language === 'es' ? 'Empleado' : 'Employee'}
                      </option>
                      <option value="candidate">
                        {language === 'es' ? 'Candidato' : 'Candidate'}
                      </option>
                    </select>
                  </div>

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'es' ? 'Nombre Completo' : 'Full Name'} *
                    </label>
                    <input
                      type="text"
                      value={newUser.nombre}
                      onChange={(e) => setNewUser({...newUser, nombre: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={language === 'es' ? 'Ej: Juan Pérez' : 'Ex: John Doe'}
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'es' ? 'Correo Electrónico' : 'Email'} *
                    </label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={language === 'es' ? 'usuario@empresa.com' : 'user@company.com'}
                      required
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'es' ? 'Contraseña' : 'Password'} *
                    </label>
                    <input
                      type="text"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={language === 'es' ? 'Mínimo 6 caracteres' : 'Minimum 6 characters'}
                      required
                      minLength={6}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      {language === 'es' 
                        ? 'Nota: La contraseña se muestra en texto plano. El usuario debe cambiarla después del primer login.' 
                        : 'Note: Password is shown in plain text. User should change it after first login.'}
                    </p>
                  </div>

                  {/* Department (optional for employees) */}
                  {newUser.rol === 'employee' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'es' ? 'Departamento' : 'Department'} ({language === 'es' ? 'opcional' : 'optional'})
                      </label>
                      <input
                        type="text"
                        value={newUser.departamento}
                        onChange={(e) => setNewUser({...newUser, departamento: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={language === 'es' ? 'Ej: Recursos Humanos' : 'Ex: Human Resources'}
                      />
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="flex justify-end space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setNewUser({
                        nombre: '',
                        email: '',
                        password: '',
                        rol: 'employee',
                        empresa_id: '',
                        departamento: ''
                      })}
                    >
                      {language === 'es' ? 'Limpiar' : 'Clear'}
                    </Button>
                    <Button
                      type="submit"
                      disabled={creatingUser}
                    >
                      {creatingUser 
                        ? (language === 'es' ? 'Creando...' : 'Creating...') 
                        : (language === 'es' ? 'Crear Usuario' : 'Create User')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          
          {/* Companies Tab */}

          {/* Transfer User Tab */}
          <TabsContent value="transfer-user">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'es' ? 'Transferir Usuario Entre Empresas' : 'Transfer User Between Companies'}</CardTitle>
                <CardDescription>
                  {language === 'es' 
                    ? 'Mueve un empleado o candidato de una empresa a otra, incluyendo todos sus assessments y resultados' 
                    : 'Move an employee or candidate from one company to another, including all their assessments and results'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* User Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'es' ? 'Seleccionar Usuario' : 'Select User'} *
                    </label>
                    <select
                      value={transferData.user_id}
                      onChange={(e) => handleSelectUserForTransfer(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">
                        {language === 'es' ? 'Seleccionar usuario...' : 'Select user...'}
                      </option>
                      {users.filter(u => u.rol !== 'super_admin').map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.nombre} ({user.email}) - {user.rol} - {user.empresa_nombre || 'Sin empresa'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Display selected user info */}
                  {selectedUserForTransfer && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">
                        {language === 'es' ? 'Usuario Seleccionado:' : 'Selected User:'}
                      </h4>
                      <div className="space-y-1 text-sm text-blue-800">
                        <p><strong>{language === 'es' ? 'Nombre:' : 'Name:'}</strong> {selectedUserForTransfer.nombre}</p>
                        <p><strong>{language === 'es' ? 'Email:' : 'Email:'}</strong> {selectedUserForTransfer.email}</p>
                        <p><strong>{language === 'es' ? 'Rol:' : 'Role:'}</strong> {selectedUserForTransfer.rol}</p>
                        <p><strong>{language === 'es' ? 'Empresa Actual:' : 'Current Company:'}</strong> {selectedUserForTransfer.empresa_nombre || 'N/A'}</p>
                      </div>
                    </div>
                  )}

                  {/* From Company (auto-filled) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'es' ? 'Empresa Origen' : 'Source Company'}
                    </label>
                    <input
                      type="text"
                      value={selectedUserForTransfer?.empresa_nombre || (language === 'es' ? 'Selecciona un usuario primero' : 'Select a user first')}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                    />
                  </div>

                  {/* To Company Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'es' ? 'Empresa Destino' : 'Target Company'} *
                    </label>
                    <select
                      value={transferData.to_company_id}
                      onChange={(e) => setTransferData({...transferData, to_company_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={!selectedUserForTransfer}
                    >
                      <option value="">
                        {language === 'es' ? 'Seleccionar empresa destino...' : 'Select target company...'}
                      </option>
                      {companies
                        .filter(c => c.id !== transferData.from_company_id)
                        .map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.nombre} ({company.codigo_acceso})
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Warning Message */}
                  {transferData.user_id && transferData.to_company_id && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">
                            {language === 'es' ? '⚠️ Advertencia Importante' : '⚠️ Important Warning'}
                          </h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p>
                              {t('assessment_ui.transfer_warning', 'Esta acción transferirá el usuario Y TODOS sus datos (assessments, resultados, dimensiones) a la nueva empresa. Esta acción NO se puede deshacer.')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Confirmation Checkbox */}
                  {transferData.user_id && transferData.to_company_id && (
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="confirm-transfer"
                        checked={showTransferConfirm}
                        onChange={(e) => setShowTransferConfirm(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="confirm-transfer" className="text-sm font-medium text-gray-700">
                        {language === 'es'
                          ? 'Confirmo que quiero transferir este usuario y todos sus datos'
                          : 'I confirm that I want to transfer this user and all their data'}
                      </label>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setTransferData({
                          user_id: '',
                          from_company_id: '',
                          to_company_id: ''
                        });
                        setSelectedUserForTransfer(null);
                        setShowTransferConfirm(false);
                      }}
                    >
                      {language === 'es' ? 'Cancelar' : 'Cancel'}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleTransferUser}
                      disabled={transferring || !showTransferConfirm || !transferData.user_id || !transferData.to_company_id}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      {transferring 
                        ? (language === 'es' ? 'Transfiriendo...' : 'Transferring...') 
                        : (language === 'es' ? 'Transferir Usuario' : 'Transfer User')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="companies">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'es' ? 'Todas las Empresas' : 'All Companies'}</CardTitle>
                <CardDescription>
                  {language === 'es' ? 'Gestión de empresas registradas' : 'Registered company management'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {companies.map((company) => (
                    <div key={company.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-medium text-gray-900">{company.nombre}</h3>
                            <Badge variant="outline">{company.codigo_acceso}</Badge>
                            <Badge variant={company.activa ? "success" : "destructive"}>
                              {company.activa ? (language === 'es' ? 'Activa' : 'Active') : (language === 'es' ? 'Inactiva' : 'Inactive')}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                            <span>
                              {language === 'es' ? 'Admin' : 'Admin'}: {company.email_admin}
                            </span>
                            <span>
                              {language === 'es' ? 'Empleados' : 'Employees'}: {company.active_employees}/{company.total_employees}
                            </span>
                            <span>
                              {language === 'es' ? 'Evaluaciones' : 'Assessments'}: {company.total_assessments}
                            </span>
                            <span>
                              {language === 'es' ? 'Registrada' : 'Registered'}: {formatDate(company.fecha_registro)}
                            </span>
                          </div>
                          
                          {company.industria && (
                            <div className="mt-2 text-sm text-gray-600">
                              {language === 'es' ? 'Industria' : 'Industry'}: {company.industria}
                              {company.tamano_empresa && ` | ${language === 'es' ? 'Tamaño' : 'Size'}: ${company.tamano_empresa}`}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <SubscriptionManagement />
          </TabsContent>
          
          {/* Identity Profiles Tab */}
          <TabsContent value="identity-profiles">
            <IdentityProfilesManagement />
          </TabsContent>
          
          {/* Consent Management Tab */}
          <TabsContent value="consent">
            <ConsentManagement />
          </TabsContent>
          
          {/* Assessments Tab */}
          <TabsContent value="assessments">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'es' ? 'Evaluaciones Recientes' : 'Recent Assessments'}</CardTitle>
                <CardDescription>
                  {language === 'es' ? 'Últimas evaluaciones en el sistema' : 'Latest assessments in the system'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardData && (
                  <div className="space-y-4">
                    {dashboardData.recent_assessments.map((assessment, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-medium text-gray-900">
                                {assessment.tipo.replace('_', ' ').toUpperCase()}
                              </h3>
                              <Badge variant={assessment.status === 'completado' ? 'success' : 'secondary'}>
                                {assessment.status}
                              </Badge>
                            </div>
                            
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span>{language === 'es' ? 'Usuario' : 'User'}: {assessment.usuario_nombre}</span>
                              {assessment.empresa_nombre && (
                                <span>{language === 'es' ? 'Empresa' : 'Company'}: {assessment.empresa_nombre}</span>
                              )}
                              <span>
                                {language === 'es' ? 'Progreso' : 'Progress'}: {Math.round(assessment.progreso_porcentaje)}%
                              </span>
                              <span>
                                {language === 'es' ? 'Iniciado' : 'Started'}: {formatDate(assessment.fecha_inicio)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;